import React from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import { getFallbackProductImage, resolveProductImage } from '../../../data/productsApi';
import { getOrderStatusMeta } from '../../../utils/orderStatus';

type OrderItem = {
  _id: string;
  productId: string;
  productName: string;
  image?: string;
  quantity: number;
  price: number;
};

type OrderData = {
  _id: string;
  orderNumber: string;
  customerInfo: {
    name: string;
    email?: string | null;
    phone: string;
    address: string;
  };
  items: OrderItem[];
  totalAmount: number;
  status: string;
  paymentStatus: 'paid' | 'unpaid';
  paymentMethod?: string;
  trackingNumber?: string;
  deliveryBoyUserId?: string;
  deliveryNote?: string;
  failureReason?: string;
  proofOfDeliveryUrl?: string;
  deliveryBoy?: {
    id: string;
    name?: string;
    phoneNumber?: string | null;
  } | null;
  createdAt?: string;
};

type ViewOrderModalProps = {
  show: boolean;
  setShow: (value: boolean) => void;
  order: OrderData | null;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ViewOrderModal = ({ show, setShow, order }: ViewOrderModalProps) => {
  const statusMeta = getOrderStatusMeta(order?.status);

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Order Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!order ? (
          <div className="text-muted">No order selected.</div>
        ) : (
          <div className="d-grid gap-3">
            <div className="d-flex justify-content-between flex-wrap gap-2">
              <div>
                <div className="fw-semibold">Order #</div>
                <div>{order.orderNumber}</div>
              </div>
              <div>
                <div className="fw-semibold">Created At</div>
                <div>{formatDate(order.createdAt)}</div>
              </div>
              <div>
                <div className="fw-semibold">Status</div>
                <Badge bg={statusMeta.badge}>{statusMeta.label}</Badge>
              </div>
              <div>
                <div className="fw-semibold">Payment</div>
                <Badge bg={order.paymentStatus === 'paid' ? 'success' : 'warning'}>{order.paymentStatus}</Badge>
              </div>
            </div>

            <div className="border rounded p-3">
              <h6 className="mb-2">Customer</h6>
              <div><strong>{order.customerInfo?.name || 'Guest'}</strong></div>
              <div>{order.customerInfo?.phone || '-'}</div>
              <div>{order.customerInfo?.email || '-'}</div>
              <div className="mt-1">{order.customerInfo?.address || '-'}</div>
            </div>

            <div className="border rounded p-3">
              <h6 className="mb-2">Items</h6>
              <Table bordered size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(order.items) && order.items.length > 0 ? (
                    order.items.map((item) => (
                      <tr key={item._id || `${item.productId}-${item.productName}`}>
                        <td style={{ width: '84px' }}>
                          <img
                            src={resolveProductImage(item.image)}
                            alt={item.productName}
                            style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }}
                            onError={(event) => {
                              const target = event.currentTarget;
                              if (target.src !== getFallbackProductImage()) {
                                target.src = getFallbackProductImage();
                              }
                            }}
                          />
                        </td>
                        <td>{item.productName}</td>
                        <td>{item.quantity}</td>
                        <td>Rs {Number(item.price || 0).toFixed(2)}</td>
                        <td>Rs {(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">No items found.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div className="mt-2 text-end">
                <strong>Total: Rs {Number(order.totalAmount || 0).toFixed(2)}</strong>
              </div>
            </div>

            <div className="border rounded p-3">
              <h6 className="mb-2">Delivery</h6>
              <div>Delivery Boy: {order.deliveryBoy?.name || 'Unassigned'}</div>
              <div>Tracking: {order.trackingNumber || 'N/A'}</div>
              <div>Payment Method: {order.paymentMethod || 'N/A'}</div>
              {order.deliveryNote ? <div className="mt-1">Note: {order.deliveryNote}</div> : null}
              {order.failureReason ? <div className="mt-1 text-danger">Failure Reason: {order.failureReason}</div> : null}
              {order.proofOfDeliveryUrl ? (
                <div className="mt-1">
                  Proof: <a href={order.proofOfDeliveryUrl} target="_blank" rel="noreferrer">Open proof</a>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewOrderModal;
